import { computed, ComputedRef } from "vue";
import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";
import { useProductsStore } from "@/stores/Products";

// UTILITIES
// import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
// const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export function useMenuPermissions() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();
    const {
        hasArticleAccess,
        hasBonusReportAccess,
        hasCourseAccess,
        hasDashboardAccess,
        hasEtfListingAccess,
        hasEquityCalculatorAccess,
        hasMainSearchAccess,
        hasMajorIndexBreadthAccess,
        hasMyLibraryAccess,
        hasNyseBpiAccess,
        hasPortfolioBuilderAccess,
        hasPositionSizeCalculatorAccess,
        hasProductTradeAlertsAccess,
        hasProfileAccess,
        hasRelativeStrengthMatrixAccess,
        hasSectorAccess,
        hasSectorAlertsAccess,
        hasSectorBpiAccess,
        hasSpreadCalculatorAccess,
        hasToolsAccess,
        hasWebinarAccess
    } = storeToRefs( permissionsStore );
    const productsStore = useProductsStore();

    function hasAccessBasedOnShortCode( shortCode: string | undefined ): boolean {
        let hasAccess: boolean = false;
        if ( shortCode ) {
            hasAccess = permissionsStore.hasAccessBasedOnShortCode( shortCode );
        }

        return hasAccess;
    }

    // Main Search Permissions
    const showMainSearch = computed< boolean >( () => {
        return hasMainSearchAccess.value;
    } );

    // Dashboard Permissions
    const showDashboard: ComputedRef< boolean > = computed( () => isAuthenticated.value && hasDashboardAccess.value );

    // Products Menu Permissions
    const showProductsMenuItem: ComputedRef< boolean > = computed( (): boolean => productsStore.allProducts.length > 0 );

    // Alerts Menu Permissions
    const showAlertsMenuItem: ComputedRef< boolean > = computed( () => isAuthenticated.value && ( showSectorAlertsMenuOption.value || showTradeAlertsMenuOption.value ) );
    const showSectorAlertsMenuOption: ComputedRef< boolean > = computed( () => hasSectorAlertsAccess.value && hasSectorAccess.value );
    const showTradeAlertsMenuOption: ComputedRef< boolean > = computed( () => hasProductTradeAlertsAccess.value );

    // Tools Menu Permissions
    const showToolsMenuItem: ComputedRef< boolean > = computed( () => isAuthenticated.value && hasToolsAccess.value );

    // -- Indicators
    const showIndicatorsSection: ComputedRef< boolean > = computed( () => showSectorBpiMenuOption.value || showNyseBpiMenuOption.value || showRelativeStrengthMatrixMenuOption.value || showMajorIndexBreadthMenuOption.value );
    const showSectorBpiMenuOption: ComputedRef< boolean > = computed( () => hasSectorBpiAccess.value );
    const showNyseBpiMenuOption: ComputedRef< boolean > = computed( () => hasNyseBpiAccess.value );
    const showRelativeStrengthMatrixMenuOption: ComputedRef< boolean > = computed( () => hasRelativeStrengthMatrixAccess.value );
    const showMajorIndexBreadthMenuOption: ComputedRef< boolean > = computed( () => hasMajorIndexBreadthAccess.value );

    // -- Reference Tools
    const showReferenceToolsSection: ComputedRef< boolean > = computed( () => showEtfListingMenuOption.value || showSectorTreeMenuOption.value || showSectorsMenuOption.value );
    const showEtfListingMenuOption: ComputedRef< boolean > = computed( () => hasEtfListingAccess.value );
    const showSectorTreeMenuOption: ComputedRef< boolean > = computed( () => hasSectorAccess.value );
    const showSectorsMenuOption: ComputedRef< boolean > = computed( () => hasSectorAccess.value );

    // -- Portfolio Tools
    const showPortfolioToolsSection: ComputedRef< boolean > = computed( () => showPortfolioBuilderMenuOption.value || showEquityCalculatorMenuOption.value || showOptionsCalculatorMenuOption.value || showPositionSizeCalculatorMenuOption.value || showSpreadCalculatorMenuOption.value );
    const showPortfolioBuilderMenuOption: ComputedRef< boolean > = computed( () => hasPortfolioBuilderAccess.value );
    // TODO: when the options calculator is re-instated, remove this and uncomment the version below this
    const showOptionsCalculatorMenuOption: ComputedRef< boolean > = computed( () => false );
    // const showOptionsCalculatorMenuOption: ComputedRef< boolean > = computed( () => hasOptionsCalculatorAccess );
    const showPositionSizeCalculatorMenuOption: ComputedRef< boolean > = computed( () => hasPositionSizeCalculatorAccess.value );
    const showSpreadCalculatorMenuOption: ComputedRef< boolean > = computed( () => hasSpreadCalculatorAccess.value );

    // Learning Menu Permissions
    const showLearningMenuItem = computed< boolean >( () => showArticlesOption.value || showBonusReportOption.value || showCoursesMenuOption.value || showWebinarsMenuOption.value );
    const showArticlesOption = computed< boolean >( () => hasArticleAccess.value );
    const showBonusReportOption = computed< boolean >( () => hasBonusReportAccess.value );
    const showCoursesMenuOption = computed< boolean >( () => hasCourseAccess.value );
    const showWebinarsMenuOption = computed< boolean >( () => hasWebinarAccess.value );

    // User Profile Permissions
    const showMyLibrary = computed< boolean >( () => hasMyLibraryAccess.value );
    const showProfileDropdown = computed< boolean >( () => hasProfileAccess.value );

    // TEMPORARILY DISABLED - it no worky...
    const showEquityCalculatorMenuOption: ComputedRef< boolean > = computed( () => hasEquityCalculatorAccess.value );

    return {
        hasAccessBasedOnShortCode,
        showMyLibrary,
        showProfileDropdown,
        showBonusReportOption,
        showCoursesMenuOption,
        showDashboard,
        showEtfListingMenuOption,
        showEquityCalculatorMenuOption,
        showLearningMenuItem,
        showMainSearch,
        showMajorIndexBreadthMenuOption,
        showNyseBpiMenuOption,
        showOptionsCalculatorMenuOption,
        showPortfolioBuilderMenuOption,
        showPositionSizeCalculatorMenuOption,
        showRelativeStrengthMatrixMenuOption,
        showSectorAlertsMenuOption,
        showSectorBpiMenuOption,
        showSectorsMenuOption,
        showSectorTreeMenuOption,
        showSpreadCalculatorMenuOption,
        showToolsMenuItem,
        showTradeAlertsMenuOption,
        showWebinarsMenuOption,

        showProductsMenuItem,
        showAlertsMenuItem,
        showIndicatorsSection,
        showPortfolioToolsSection,
        showReferenceToolsSection
    };
}
